import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ContextIcon } from '@/components/ui/ContextIcon'
import { useTranslation, useAssetTranslation } from '@/contexts/TranslationContext'
// Layout-only mode: do not call backend yet — show mock holdings for layout
import { useUnit } from '@/contexts/UnitContext'

type TabKey = 'position' | 'movement'

export function AssetPage() {
  const params = useParams()
  const assetKey = params.assetKey || ''
  const { t } = useTranslation()
  const { ta } = useAssetTranslation(assetKey) // ta now bound to this asset group
  const router = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('position')
  const { currentUnit } = useUnit()
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  // Read-only stock screen for now. Edit/create/delete are planned later.
  const handleNew = () => {
    alert(ta('comingSoon') || 'Funcionalidade em desenvolvimento')
  }
  const handleEdit = (id?: string) => {
    alert(ta('comingSoon') || 'Funcionalidade em desenvolvimento')
  }
  const handleDelete = (id?: string) => {
    alert(ta('comingSoon') || 'Funcionalidade em desenvolvimento')
  }

  const loadItems = async () => {
    setLoading(true)
    // Mock data for layout-only mode (no DB connection)
    const mock = [
      { id: 'h1', name: ta('record.examples.item1') || 'Exemplo 1', qty: 24, created_at: new Date().toISOString() },
      { id: 'h2', name: ta('record.examples.item2') || 'Exemplo 2', qty: 12, created_at: new Date().toISOString() },
      { id: 'h3', name: ta('record.examples.item3') || 'Exemplo 3', qty: 6, created_at: new Date().toISOString() },
    ]
    // small timeout to simulate loading UX
    setTimeout(() => {
      setItems(mock)
      setLoading(false)
    }, 120)
  }

  useEffect(() => {
    loadItems()
    // reload when unit changes
  }, [assetKey, currentUnit?.id])

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
            <ContextIcon context={(import.meta.env.VITE_PUBLIC_APP_CONTEXT as any) || 'farm'} className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{ta('list.title')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">{ta('list.description')}</p>
          </div>
        </div>
        <button
          disabled
          title="Em breve"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium opacity-50 cursor-not-allowed"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {ta('new')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
        <button
          className={`px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border-b-2 transition-colors ${
            activeTab === 'position'
              ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
          onClick={() => setActiveTab('position')}
        >
          {ta('tabs.position')}
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border-b-2 transition-colors ${
            activeTab === 'movement'
              ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
          onClick={() => setActiveTab('movement')}
        >
          {ta('tabs.movement')}
        </button>
      </div>

      {activeTab === 'position' ? (
        <PositionTab onEdit={handleEdit} onDelete={handleDelete} assetKey={assetKey} items={items} loading={loading} currentUnit={currentUnit} ta={ta} />
      ) : (
        <MovementTab onEdit={handleEdit} onDelete={handleDelete} assetKey={assetKey} items={items} loading={loading} currentUnit={currentUnit} ta={ta} />
      )}
    </div>
  )
}

function PositionTab({ onEdit, onDelete, assetKey, items, loading, currentUnit, ta }: any) {
  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-500">
          <svg className="h-12 w-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4" />
          </svg>
          <p className="text-sm">{ta('noItems') || 'Nenhum item encontrado'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it: any) => (
            <div key={it.id} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg transition-shadow">
              <div className="mb-3">
                <div className="font-semibold text-slate-900 dark:text-white text-base">{it.name || it.asset_name || it.asset_key}</div>
                {currentUnit?.name && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{currentUnit.name}</div>
                )}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                {ta('stockQty')}: <span className="font-semibold text-slate-900 dark:text-white">{it.qty ?? it.quantity ?? it.amount ?? 0}</span>
              </div>
              {it.created_at && (
                <div className="text-xs text-slate-400 mb-3">{new Date(it.created_at).toLocaleDateString()}</div>
              )}
              <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => onEdit(it.id)}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  {ta('edit')}
                </button>
                <button
                  onClick={() => onDelete(it.id)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  {ta('delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MovementTab({ onEdit, onDelete, assetKey, items, loading, ta }: any) {
  const movements = items.length > 0
    ? items.map((it: any) => ({ id: `mv-${it.id}`, desc: it.name, date: it.created_at || '2026-01-01' }))
    : [
        { id: 'm1', desc: ta('record.examples.m1'), date: '2026-01-01' },
        { id: 'm2', desc: ta('record.examples.m2'), date: '2026-01-05' },
      ]
  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {movements.map((mv: any) => (
            <div key={mv.id} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-5 py-4 flex items-center justify-between hover:shadow-md transition-shadow">
              <div>
                <div className="font-semibold text-slate-900 dark:text-white">{mv.desc}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{mv.date}</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onEdit(mv.id)}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  {ta('edit')}
                </button>
                <button
                  onClick={() => onDelete(mv.id)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  {ta('delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
